import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { Box } from '@mui/material';
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';
import { type DateRangePresetId, type DateRangeValue } from './date-range-presets';
import { dateRangeLabelToNow, dateRangePresetMessages } from './date-range-selector-messages';

export interface DateRangeTriggerLabelProps {
  /** Current range (used to format custom range labels). */
  value?: DateRangeValue | null;
  /** Current preset id (determines which label to show). */
  presetId?: DateRangePresetId | null;
  /** Preset used when presetId is not provided. */
  defaultPreset?: DateRangePresetId;
}

const DEFAULT_PRESET: DateRangePresetId = 'today';

const dateFormatProps = {
  month: 'short' as const,
  day: 'numeric' as const,
  year: 'numeric' as const,
};

/**
 * Renders the date range selector trigger content: calendar icon plus
 * a label derived from the current preset or custom range (e.g. "Hoy",
 * "14 de febrero de 2025 - 14 de febrero de 2025").
 * Uses FormattedDate / FormattedMessage from react-intl for locale-aware formatting.
 */
export function DateRangeTriggerLabel({
  value,
  presetId,
  defaultPreset = DEFAULT_PRESET,
}: DateRangeTriggerLabelProps) {
  const { $t } = useIntl();
  const effectivePreset = presetId ?? defaultPreset;

  const label =
    effectivePreset === 'fromCustomDateUntilNow' && value?.from ? (
      <FormattedMessage
        {...dateRangeLabelToNow}
        values={{
          from: <FormattedDate value={value.from} {...dateFormatProps} />,
        }}
      />
    ) : effectivePreset === 'customFixedDateRange' && value?.from && value?.to ? (
      <>
        <FormattedDate value={value.from} {...dateFormatProps} />
        {' - '}
        <FormattedDate value={value.to} {...dateFormatProps} />
      </>
    ) : (
      $t(dateRangePresetMessages[effectivePreset])
    );

  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
      <CalendarMonthIcon fontSize="small" />
      {label}
    </Box>
  );
}
